import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MessageCircle, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const STATUS = ["new", "contacted", "converted", "closed"];
const statusColor = (s) => ({
  new: "border-blue-300 text-blue-700",
  contacted: "border-amber-300 text-amber-700",
  converted: "border-emerald-300 text-emerald-700",
  closed: "border-slate-400 text-slate-500",
}[s] || "border-slate-300 text-slate-600");

export default function SuperEnquiries() {
  const [items, setItems] = useState([]);
  const [schools, setSchools] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [schoolFilter, setSchoolFilter] = useState("all");

  const load = async () => {
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const [e, s] = await Promise.all([
        api.get("/enquiries", { params }),
        api.get("/schools?limit=200"),
      ]);
      setItems(e.data);
      setSchools(s.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const schoolName = (id) => schools.find((s) => s.id === id)?.name || id?.slice(-6) || "—";

  const filtered = items.filter((e) => {
    if (schoolFilter !== "all" && e.school_id !== schoolFilter) return false;
    if (!q) return true;
    const hay = `${e.name} ${e.email} ${e.phone} ${e.class_interested} ${schoolName(e.school_id)}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const mark = async (id, status) => {
    try { await api.put(`/enquiries/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const bySchool = filtered.reduce((acc, e) => { acc[e.school_id] = (acc[e.school_id] || 0) + 1; return acc; }, {});
  const topSchools = Object.entries(bySchool).sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <DashLayout variant="super" title="Parent Enquiries — Leads">
      <div className="text-sm text-slate-500 mb-4">Admission enquiries submitted by parents from school detail pages. Each row shows which school the enquiry targeted.</div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Total" value={filtered.length} testId="enq-kpi-total" color="bg-blue-50 text-blue-700" />
        <Kpi label="New" value={filtered.filter(e => e.status === "new").length} testId="enq-kpi-new" color="bg-amber-50 text-amber-700" />
        <Kpi label="Converted" value={filtered.filter(e => e.status === "converted").length} testId="enq-kpi-converted" color="bg-emerald-50 text-emerald-700" />
        <Kpi label="Closed" value={filtered.filter(e => e.status === "closed").length} testId="enq-kpi-closed" color="bg-slate-100 text-slate-600" />
      </div>

      {topSchools.length > 0 && (
        <Card className="p-4 mb-4" data-testid="enq-top-schools">
          <h3 className="font-bold text-sm mb-2">Top schools by lead volume</h3>
          <div className="flex flex-wrap gap-2">
            {topSchools.map(([sid, n]) => (
              <Badge key={sid} variant="outline" className="text-xs">{schoolName(sid)} · {n}</Badge>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-3 flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <Input placeholder="Search name / email / phone / school" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="enq-search" />
        </div>
        <Select value={schoolFilter} onValueChange={setSchoolFilter}>
          <SelectTrigger className="w-56" data-testid="enq-filter-school"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All Schools</SelectItem>
            {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="enq-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="hidden md:block">
          <Table>
            <TableHeader><TableRow>
              <TableHead>School</TableHead><TableHead>Parent</TableHead><TableHead>Class</TableHead>
              <TableHead>Contact</TableHead><TableHead>Received</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} data-testid={`enq-row-${e.id}`}>
                  <TableCell className="font-semibold">{schoolName(e.school_id)}</TableCell>
                  <TableCell><div>{e.name}</div>{e.message && <div className="text-xs text-slate-500 max-w-[300px] truncate">{e.message}</div>}</TableCell>
                  <TableCell>{e.class_interested || "—"}</TableCell>
                  <TableCell className="text-xs">
                    {e.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {e.phone}</div>}
                    {e.email && <div className="flex items-center gap-1 text-slate-500"><Mail className="w-3 h-3" /> {e.email}</div>}
                  </TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{(e.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell><Badge variant="outline" className={statusColor(e.status)}>{e.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    {e.phone && <Button size="sm" variant="outline" onClick={() => window.open(`https://wa.me/${String(e.phone).replace(/\D/g, "")}`, "_blank")} data-testid={`enq-wa-${e.id}`}><MessageCircle className="w-4 h-4 text-emerald-600" /></Button>}
                    <Select value={e.status} onValueChange={(v) => mark(e.id, v)}>
                      <SelectTrigger className="w-32 inline-flex" data-testid={`enq-status-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No enquiries yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <div className="md:hidden divide-y divide-slate-100">
          {filtered.map((e) => (
            <div key={e.id} className="p-4" data-testid={`enq-card-${e.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{e.name}</div>
                  <div className="text-xs text-slate-500">for <b>{schoolName(e.school_id)}</b> · Class {e.class_interested || "—"}</div>
                </div>
                <Badge variant="outline" className={statusColor(e.status)}>{e.status}</Badge>
              </div>
              {e.message && <div className="text-xs text-slate-600 mt-2">{e.message}</div>}
              <div className="flex flex-wrap gap-3 text-xs mt-2 text-slate-600">
                {e.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {e.phone}</span>}
                {e.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {e.email}</span>}
                <span className="text-slate-400 ml-auto font-mono">{(e.created_at || "").slice(0, 10)}</span>
              </div>
              <div className="flex gap-2 mt-3">
                {e.phone && <Button size="sm" variant="outline" className="flex-1" onClick={() => window.open(`https://wa.me/${String(e.phone).replace(/\D/g, "")}`, "_blank")} data-testid={`enq-card-wa-${e.id}`}><MessageCircle className="w-4 h-4 mr-1 text-emerald-600" />WhatsApp</Button>}
                <Select value={e.status} onValueChange={(v) => mark(e.id, v)}>
                  <SelectTrigger className="flex-1" data-testid={`enq-card-status-${e.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-10 text-center text-slate-500 text-sm">No enquiries yet</div>}
        </div>
      </Card>
    </DashLayout>
  );
}

const Kpi = ({ label, value, color, testId }) => (
  <Card className="p-3" data-testid={testId}>
    <div className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${color}`}>{label}</div>
    <div className="text-2xl font-black mt-1 tabular-nums">{value}</div>
  </Card>
);
